/**
 * Suggestion Dialog
 * Displays user tickets and allows submitting new suggestions/issues
 * Styled to match other settings dialogs (bottom sheet on mobile, centered on desktop)
 */

import { useState, useCallback, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ArrowLeft, SuccessCircleIcon, Loader2 } from '@/components/icons'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/context/auth-context'
import { useApi } from '@/hooks/useApi'

interface SuggestionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface Ticket {
  id: string
  user_id: string
  type: 'suggestion' | 'issue'
  message: string
  status: 'open' | 'replied'
  created_at: string
  updated_at: string
}

type DialogView = 'list' | 'form' | 'success'

/**
 * Format relative time for ticket timestamps
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  
  return date.toLocaleDateString()
}

/**
 * Single ticket item
 */
function TicketItem({ ticket }: { ticket: Ticket }) {
  return (
    <div className="p-3 border rounded-lg bg-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge 
              variant={ticket.type === 'issue' ? 'destructive' : 'secondary'}
              className="text-[10px] px-1.5 py-0 capitalize"
            >
              {ticket.type}
            </Badge>
            <Badge 
              variant={ticket.status === 'replied' ? 'default' : 'outline'}
              className="text-[10px] px-1.5 py-0 capitalize"
            >
              {ticket.status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1 break-words line-clamp-3">
            {ticket.message}
          </p>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
          {formatRelativeTime(ticket.created_at)}
        </span>
      </div>
    </div>
  )
}

/**
 * Empty state when no tickets
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <p className="text-sm text-muted-foreground">No tickets yet</p>
      <p className="text-xs text-muted-foreground mt-1">Submit your first suggestion or report an issue</p>
    </div>
  )
}

/**
 * Ticket list view
 */
function TicketList({ tickets, loading }: { tickets: Ticket[]; loading: boolean }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (tickets.length === 0) {
    return <EmptyState />
  }

  return (
    <div className="space-y-2 overflow-y-auto">
      {tickets.map((ticket) => (
        <TicketItem key={ticket.id} ticket={ticket} />
      ))}
    </div>
  )
}

/**
 * Success view after submitting
 */
function SuccessView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <SuccessCircleIcon className="h-16 w-16 text-green-500 mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">Thank you!</h3>
      <p className="text-sm text-muted-foreground max-w-[280px]">
        Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.
      </p>
      <Button 
        variant="secondary" 
        onClick={onClose}
        className="mt-6"
      >
        Close
      </Button>
    </div>
  )
}

/**
 * Form view for submitting new ticket
 */
function TicketForm({ 
  onSubmit, 
  onCancel, 
  loading 
}: { 
  onSubmit: (type: 'suggestion' | 'issue', message: string) => Promise<void>
  onCancel: () => void
  loading: boolean
}) {
  const [type, setType] = useState<'suggestion' | 'issue'>('suggestion')
  const [message, setMessage] = useState('')

  const handleSubmit = async () => {
    if (!message.trim()) return
    await onSubmit(type, message.trim())
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="type">Type</Label>
        <Select value={type} onValueChange={(value: 'suggestion' | 'issue') => setType(value)}>
          <SelectTrigger className="shadow-none">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="suggestion">Suggestion</SelectItem>
            <SelectItem value="issue">Issue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          placeholder={type === 'suggestion' 
            ? "Share your idea or suggestion..." 
            : "Describe the issue you're experiencing..."
          }
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="min-h-[120px] resize-none py-2"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button 
          variant="secondary" 
          onClick={onCancel}
          className="flex-1"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          className="flex-1"
          disabled={!message.trim() || loading}
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Sending...
            </>
          ) : (
            'Send'
          )}
        </Button>
      </div>
    </div>
  )
}

export function SuggestionDialog({ open, onOpenChange }: SuggestionDialogProps) {
  const { user } = useAuth()
  const { post } = useApi()
  const [view, setView] = useState<DialogView>('list')
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Fetch tickets when dialog opens
  const fetchTickets = useCallback(async () => {
    if (!user) return
    
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setTickets(data || [])
    } catch (err) {
      console.error('Failed to fetch tickets:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    if (open) {
      fetchTickets()
      setView('list')
    }
  }, [open, fetchTickets])

  // Submit new ticket via edge function
  const handleSubmit = async (type: 'suggestion' | 'issue', message: string) => {
    if (!user) return
    
    setSubmitting(true)
    try {
      const response = await post<{ success: boolean; ticket?: Ticket; error?: string }>(
        'https://n8n.calintent.com/webhook/api/suggestion',
        { type, message }
      )

      if (!response.success || !response.data?.success) {
        throw new Error(response.data?.error || response.error || 'Failed to submit ticket')
      }
      
      setView('success')
      // Refresh tickets list for when user returns to list view
      fetchTickets()
    } catch (err) {
      console.error('Failed to submit ticket:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  const handleBack = () => {
    if (view === 'form' || view === 'success') {
      setView('list')
    } else {
      handleClose()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[70vh] max-h-[70vh]
          md:w-[500px] md:h-auto md:max-h-[60vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogDescription className="sr-only">Submit suggestions or report issues.</DialogDescription>
        
        {view !== 'success' && (
          <DialogHeader className="px-4 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              {/* Back Button */}
              <Button
                variant="secondary"
                onClick={handleBack}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>
              
              <DialogTitle className="flex-1 text-center">
                {view === 'form' ? 'New Ticket' : 'Tickets'}
              </DialogTitle>
              
              {/* Spacer to balance the layout */}
              <div className="w-[68px] md:w-[76px]"></div>
            </div>
          </DialogHeader>
        )}

        <div className="px-4 pb-6 overflow-y-auto flex-1 min-h-0">
          {view === 'list' && (
            <TicketList tickets={tickets} loading={loading} />
          )}
          {view === 'form' && (
            <TicketForm 
              onSubmit={handleSubmit} 
              onCancel={() => setView('list')}
              loading={submitting}
            />
          )}
          {view === 'success' && (
            <SuccessView onClose={handleClose} />
          )}
        </div>

        {/* Footer with Send Suggestion button - only on list view */}
        {view === 'list' && !loading && (
          <div className="px-4 pb-6 pt-2">
            <Button 
              onClick={() => setView('form')}
              className="w-full"
            >
              Send suggestion
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

